import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, ButtonGroup, Divider, TextField, Typography, useTheme } from '@mui/material';
import { getSystemAPI, postUploadFaqAPI, postUploadManualAPI, putUpdateRemindTimeAPI } from 'apis/settings/settingsAPI';
import { downloadFile } from '../../apis/file/fileAPI';
import { postEmailTemplateAPI, postEmailTemplateTestAPI } from '../../apis/emailTemplate/emailTemplateAPI';
import './MenuCommon.css';
import Tiptap from '../Tiptap/Tiptap';
import { useTranslation } from 'react-i18next';
import File from './UploadFileBtn';
import UploadFileBtn from './UploadFileBtn';
import { useSnackbarStore } from 'store/useSnackbarStore';
import SnackbarAlert from 'components/SnackbarAlert';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

// 첫 번째 텍스트 노드의 연속된 공백을 &nbsp;로 변환하는 함수 (TypeScript-safe)
const preserveLeadingSpaces = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const replaceLeadingSpaces = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      node.textContent = node.textContent.replace(/^\s+/, (spaces) => spaces.replace(/ /g, '\u00A0'));
    } else {
      Array.from(node.childNodes).forEach(replaceLeadingSpaces);
    }
  };

  Array.from(doc.body.childNodes).forEach(replaceLeadingSpaces);

  return doc.body.innerHTML;
};

interface IManualFaq {
  manualFileName: string;
  faqFileName: string;
  manualFileNameEnglish: string;
  faqFileNameEnglish: string;
  manualFileNameJapanese: string;
  faqFileNameJapanese: string;
}

type TemplateType =
  | 'VERIFY_CODE'
  | 'VISIT_REQUEST'
  | 'VISIT_REQUEST_APPROVE'
  | 'VISIT_REQUEST_REJECT'
  | 'VISIT_REQUEST_CANCEL'
  | 'SECURITY_CARD_ISSUE'
  | 'PASSWORD_INITIALIZE'
  | 'REMIND';
const templateVariable = (title: string, variable: string, example: string) => (
  <Box display="flex" flexDirection="row" gap={1} alignItems="center">
    <Typography variant="body2">{title}: </Typography>
    <Typography variant="body2" fontWeight="bold">{`{{${variable}}}`}</Typography>
    <Typography variant="body2" color="primary">
      예시) {example}
    </Typography>
  </Box>
);
function templateDescription(templateSelectedType: TemplateType, defaultRemindTime: Dayjs | null) {
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  const [remindTime, setRemindTime] = useState<Dayjs | null>(defaultRemindTime);
  const [isButtonSaveRemindTimeDisabled, setIsButtonSaveRemindTimeDisabled] = useState(false);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  useEffect(() => {
    if (!remindTime) {
      setRemindTime(defaultRemindTime);
    }
  }, [defaultRemindTime]);

  const handleRemindTimeChange = (time: Dayjs | null) => {
    setRemindTime(time);
  }

  const handleSaveRemindTime = async () => {
    try {
      setIsButtonSaveRemindTimeDisabled(true);
      setTimeout(() => {
        setIsButtonSaveRemindTimeDisabled(false);
      }, 2000);

      const response = await putUpdateRemindTimeAPI(remindTime?.isValid() ? remindTime.format('HH:mm:ss') : '');
      if (response.status === 200) {
        showSnackbar(`리마인드 시간 저장 성공`, 'success');
      }
    } catch (error) {
      showSnackbar(`리마인드 시간 저장 실패`, 'error');
    }
  };

  const template = {
    VERIFY_CODE: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.verify_code')}</Typography>
        <Divider />
        {templateVariable('인증코드', 'code', '123456')}
      </Box>
    ),
    VISIT_REQUEST: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.visit_request')}</Typography>
        <Divider />
        {templateVariable('신청자', 'requester', '홍길동(A업체)')}
        {templateVariable('신청자이메일', 'requester_email', 'hong@gil.dong')}
        {templateVariable('사업장', 'business_place', '사업장명')}
        {templateVariable('방문일', 'date', '2000년 1월 1일 ~ 2000년 1월 2일')}
        {templateVariable('방문자', 'visitors', '홍길동(A업체)님, 김철수(B업체)님')}
        {templateVariable('목적', 'purpose', '담당자와의 미팅')}
        {templateVariable('승인버튼', 'button_approve', '[메일에서 승인하는 버튼]')}
        {templateVariable('반려버튼', 'button_reject', '[메일에서 반려하는 버튼]')}
      </Box>
    ),
    VISIT_REQUEST_APPROVE: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.visit_request_approve')}</Typography>
        <Divider />
        {templateVariable('신청자', 'requester', '홍길동')}
        {templateVariable('방문일', 'date', '2000년 1월 1일 ~ 2000년 1월 2일')}
        {templateVariable('방문자', 'visitors', '홍길동(A업체)님, 김철수(B업체)님')}
        {templateVariable('목적', 'purpose', '담당자와의 미팅')}
        {templateVariable('담당자', 'manager_name', '알박담당자')}
        {templateVariable('담당자이메일', 'manager_email', 'ulvactest@ulvac.com')}
      </Box>
    ),
    VISIT_REQUEST_REJECT: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.visit_request_reject')}</Typography>
        <Divider />
        {templateVariable('신청자', 'requester', '홍길동')}
        {templateVariable('방문일', 'date', '2000년 1월 1일 ~ 2000년 1월 2일')}
        {templateVariable('방문자', 'visitors', '홍길동(A업체)님, 김철수(B업체)님')}
        {templateVariable('목적', 'purpose', '담당자와의 미팅')}
        {templateVariable('담당자', 'manager_name', '알박담당자')}
        {templateVariable('담당자이메일', 'manager_email', 'ulvactest@ulvac.com')}
      </Box>
    ),
    VISIT_REQUEST_CANCEL: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.visit_request_cancel')}</Typography>
        <Divider />
        {templateVariable('신청자', 'requester', '홍길동(A업체)')}
        {templateVariable('사업장', 'business_place', '사업장명')}
        {templateVariable('방문일', 'date', '2000년 1월 1일 ~ 2000년 1월 2일')}
        {templateVariable('방문자', 'visitors', '홍길동(A업체)님, 김철수(B업체)님')}
        {templateVariable('목적', 'purpose', '담당자와의 미팅')}
      </Box>
    ),
    SECURITY_CARD_ISSUE: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.security_card_issue')}</Typography>
        <Divider />
        {templateVariable('방문자', 'visitor', '홍길동(A업체)')}
        {templateVariable('카드번호', 'card_number', '111100002222')}
      </Box>
    ),
    PASSWORD_INITIALIZE: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.password_reset')}</Typography>
        <Divider />
        {templateVariable('아이디', 'id', 'visitor')}
        {templateVariable('초기화된 비밀번호', 'password', 'a@3D%6g!5k')}
      </Box>
    ),
    REMIND: (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography>{t('system_settings.template_description.remind')}</Typography>
        <Divider />
        <Box display="flex" gap={1} alignItems="center">
          <Typography fontSize={14} fontWeight="bold">리마인드 시간: </Typography>
          <TimePicker
            value={remindTime}
            onChange={handleRemindTimeChange}
            ampm={false} // 24시간 형식 사용 안함
            sx={{
              height: '24px',
              width: '80px',
              '& .MuiInputBase-root': {
                height: '24px',
                paddingRight: '12px'
              },
              '& .MuiInputBase-input': {
                height: '24px',
                paddingY: '4px'
              },
              '& .MuiInputAdornment-root': {
                margin: 0
              },
              '& .MuiButtonBase-root': {
                paddingY: 0,
                paddingX: '4px'
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // 아이콘 크기 조정
              }
            }}
          />
          <Button size="small" variant="outlined" sx={{ borderRadius: '4px' }} disabled={isButtonSaveRemindTimeDisabled} onClick={handleSaveRemindTime}>저장</Button>
        </Box>
        <Divider />
        {templateVariable('담당자', 'manager_name', '알박담당자')}
        {templateVariable('미승인 건수', 'unapproved_count', '2')}
        {templateVariable('미완료 건수', 'incomplete_count', '3')}
      </Box>
    )
  };

  return templateSelectedType in template ? template[templateSelectedType] : null;
}

export default function SystemSettings() {
  const [manualFaq, setManualFaq] = useState<IManualFaq>();
  const [selectedManualFiles, setSelectedManualFiles] = useState<{ [key: string]: File | null }>({
    ko: null,
    en: null,
    ja: null
  });
  const [selectedFaqFiles, setSelectedFaqFiles] = useState<{ [key: string]: File | null }>({
    ko: null,
    en: null,
    ja: null
  });

  const templateEditorRef = useRef<any>(null);
  // const termsEnglishEditorRef = useRef<any>(null);

  const [emailTemplates, setEmailTemplates] = useState<
    {
      type: string;
      language: string;
      title: string;
      content: string;
    }[]
  >([]);
  const [templateTitle, setTemplateTitle] = useState<string>('');
  const [templateContent, setTemplateContent] = useState<string>('');
  const [templateMsg, setTemplateMsg] = useState('');
  const [templateSelectedType, setTemplateSelectedType] = useState<TemplateType>('VERIFY_CODE');
  const [templateSelectedLanguage, setTemplateSelectedLanguage] = useState('ko');
  const [disableTemplateTest, setDisableTemplateTest] = useState(false);

  const [defaultRemindTime, setDefaultRemindTime] = useState<Dayjs | null>(null);

  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const fetchSettings = async () => {
    try {
      const systemAPIResponse = await getSystemAPI();
      const { manualFaq, remindTime, emailTemplates } = systemAPIResponse.data;
      setManualFaq(manualFaq);
      setEmailTemplates(emailTemplates);

      setDefaultRemindTime(dayjs(`${dayjs().format('YYYY-MM-DD')} ${remindTime}`));

      // 이메일 템플릿
      const template = emailTemplates.find((t) => t.type == templateSelectedType && t.language == templateSelectedLanguage);
      setTemplateTitle(template?.title || '');
      setTemplateContent(preserveLeadingSpaces(template?.content || ''));
    } catch (error) {
      // console.error('데이터 가져오기 실패 :', error);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleManualFileChange = (file: File | null, language: string) => {
    // console.log('handleManualFileChange', file, language);
    if (file) setSelectedManualFiles((prevState) => ({ ...prevState, [language]: file }));
  };
  const handleFaqFileChange = (file: File | null, language: string) => {
    // console.log('handleFaqFileChange', file, language);
    if (file) setSelectedFaqFiles((prevState) => ({ ...prevState, [language]: file }));
  };

  const uploadManual = async (language: string) => {
    const file = selectedManualFiles[language];
    if (!file) {
      alert('파일을 선택하세요.');
      return;
    }
    try {
      const response = await postUploadManualAPI(language, file);
      if (response.status === 200) {
        showSnackbar(`${language} 매뉴얼 업로드 성공`, 'success');
        await fetchSettings();

        setSelectedManualFiles({
          ko: null,
          en: null,
          ja: null
        });
      }
    } catch (error) {
      showSnackbar(`${language} 매뉴얼 업로드 실패`, 'error');
      // console.error(`${language} 매뉴얼 업로드 실패:`, error);
    }
  };

  const uploadFaq = async (language: string) => {
    const file = selectedFaqFiles[language];
    if (!file) {
      showSnackbar(`파일을 선택하세요.`, 'warning');
      return;
    }
    try {
      const response = await postUploadFaqAPI(language, file);
      if (response.status === 200) {
        showSnackbar(`${language} FAQ 업로드 성공`, 'success');
        await fetchSettings();

        setSelectedFaqFiles({
          ko: null,
          en: null,
          ja: null
        });
      }
    } catch (error) {
      showSnackbar(`${language} FAQ 업로드 실패`, 'error');
      // console.error(`${language} FAQ 업로드 실패:`, error);
    }
  };

  const handleDownloadFile = (fileName: string) => {
    downloadFile(fileName);
  };

  const handleTemplateLanguageChange = (language: string) => {
    setTemplateSelectedLanguage(language);

    const template = emailTemplates.find((t) => t.type == templateSelectedType && t.language == language);
    setTemplateTitle(template?.title || '');
    setTemplateContent(preserveLeadingSpaces(template?.content || ''));
  };

  const handleTemplateTypeChange = (type: string) => {
    setTemplateSelectedType(type as TemplateType);

    const template = emailTemplates.find((t) => t.type == type && t.language == templateSelectedLanguage);
    setTemplateTitle(template?.title || '');
    setTemplateContent(preserveLeadingSpaces(template?.content || ''));
  };

  const isTemplateLanguageSelected = (language: string) => templateSelectedLanguage === language;
  const isTemplateTypeSelected = (type: string) => templateSelectedType === type;

  const handleSaveTemplate = async () => {
    const title = templateTitle.trim();
    const content = templateEditorRef?.current?.getHTML() || '';

    if (!title) {
      showSnackbar(`메일 제목을 입력해주세요.`, 'warning');
      return;
    }

    try {
      const response = await postEmailTemplateAPI(templateSelectedType, templateSelectedLanguage, title, String(content));
      if (response.status === 200) {
        setTemplateContent(preserveLeadingSpaces(templateContent || ''));
        setTemplateMsg('이메일 템플릿을 저장하였습니다.');
        setTimeout(() => {
          setTemplateMsg('');
        }, 3000);

        fetchSettings();
      }
    } catch (error) {
      showSnackbar(`템플릿 저장 실패`, 'error');
      // console.error('템플릿 저장 실패:', error);
    }
  };

  const handleTestTemplate = async () => {
    // 2초간 테스트 다시 못누름
    setDisableTemplateTest(true);
    try {
      const response = await postEmailTemplateTestAPI(templateSelectedType, templateSelectedLanguage);
      setTimeout(() => {
        setDisableTemplateTest(false);
      }, 2000);
    } catch (error) {
      showSnackbar(`템플릿 테스트 실패`, 'error');
      // console.error('템플릿 테스트 실패:', error);
    }
  };

  return (
    <>
      <SnackbarAlert />
      <Box
        padding={2}
        display="flex"
        flexDirection="column"
        gap={2}
        sx={{
          width: { xs: '100vw', sm: '100%' },
          justifyContent: 'center'
        }}
      >
        <Box
          // padding={2}
          // paddingBottom={2}
          borderRadius={1}
          // bgcolor={theme.palette.contentBlock.background}
          display="flex"
          flexDirection="column"
          gap={1}
        >
          <Typography variant="h5" fontWeight="bold">
            {t('system_settings.title')}
          </Typography>

          <Divider flexItem />
        </Box>

        {/* 매뉴얼 */}
        <Box
          padding={2}
          paddingBottom={2}
          borderRadius={1}
          bgcolor={theme.palette.contentBlock.background}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ width: '100%' }}
        >
          {/* 제목 */}
          <Box
            display="flex"
            flexDirection="row"
            gap={1}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' }, // 모바일에서는 세로 배치
              alignItems: { xs: 'flex-start', sm: 'center' } // 모바일 정렬
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              {t('system_settings.manual')}
            </Typography>
          </Box>

          {/* 매뉴얼 리스트 */}
          <Box display="flex" flexDirection="column" gap={1} padding={2} borderRadius={1} boxShadow={1} bgcolor={theme.palette.contentBlock.main}>
            {/* 한국어 매뉴얼 */}
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일에서는 column, 데스크탑에서는 row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일에서는 왼쪽 정렬
              gap={1}
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.ko')}
              </Typography>
              {manualFaq?.manualFileName ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.manualFileName)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.manualFileName}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}

              <UploadFileBtn
                title="ko-manual-file"
                onFileChange={handleManualFileChange}
                lang="ko"
                fileName={selectedManualFiles?.ko?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadManual('ko')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>

            <Box
              display="flex"
              gap={1}
              //   flexDirection="row"
              // alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일에서는 column, 데스크탑에서는 row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일에서는 왼쪽 정렬
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.en')}
              </Typography>
              {manualFaq?.manualFileNameEnglish ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.manualFileNameEnglish)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.manualFileNameEnglish}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}
              <UploadFileBtn
                title="en-manual-file"
                onFileChange={handleManualFileChange}
                lang="en"
                fileName={selectedManualFiles?.en?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadManual('en')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>

            <Box
              display="flex"
              gap={1}
              //         flexDirection="row"
              // alignItems="center"
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일: column, 데스크탑: row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일 왼쪽 정렬
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.ja')}
              </Typography>
              {manualFaq?.manualFileNameJapanese ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.manualFileNameJapanese)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.manualFileNameJapanese}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}
              <UploadFileBtn
                title="ja-manual-file"
                onFileChange={handleManualFileChange}
                lang="ja"
                fileName={selectedManualFiles?.ja?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadManual('ja')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* FAQ */}
        <Box
          padding={2}
          paddingBottom={2}
          borderRadius={1}
          bgcolor={theme.palette.contentBlock.background}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ width: '100%' }}
        >
          <Box display="flex" flexDirection="row" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              {t('system_settings.faq')}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} padding={2} borderRadius={1} boxShadow={1} bgcolor={theme.palette.contentBlock.main}>
            <Box
              display="flex"
              gap={1}
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일: column, 데스크탑: row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일 왼쪽 정렬
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.ko')}
              </Typography>
              {manualFaq?.faqFileName ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.faqFileName)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.faqFileName}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}
              <UploadFileBtn
                title="ko-faq-file"
                onFileChange={handleFaqFileChange}
                lang="ko"
                fileName={selectedFaqFiles?.ko?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadFaq('ko')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>

            <Box
              display="flex"
              gap={1}
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일: column, 데스크탑: row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일 왼쪽 정렬
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.en')}
              </Typography>
              {manualFaq?.faqFileNameEnglish ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.faqFileNameEnglish)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.faqFileNameEnglish}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}
              <UploadFileBtn
                title="en-faq-file"
                onFileChange={handleFaqFileChange}
                lang="en"
                fileName={selectedFaqFiles?.en?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadFaq('en')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>

            <Box
              display="flex"
              gap={1}
              flexDirection={{ xs: 'column', sm: 'row' }} // 모바일: column, 데스크탑: row
              alignItems={{ xs: 'flex-start', sm: 'center' }} // 모바일 왼쪽 정렬
            >
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.ja')}
              </Typography>
              {manualFaq?.faqFileNameJapanese ? (
                <Typography
                  onClick={() => handleDownloadFile(manualFaq?.faqFileNameJapanese)}
                  sx={{
                    color: theme.palette.fieldText.link,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      color: theme.palette.fieldText.linkHover,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {manualFaq?.faqFileNameJapanese}
                </Typography>
              ) : (
                <Typography variant="body1">{t('system_settings.no_upload_file')}</Typography>
              )}

              <UploadFileBtn
                title="ja-faq-file"
                onFileChange={handleFaqFileChange}
                lang="ja"
                fileName={selectedFaqFiles?.ja?.name || t('system_settings.file.no_file')}
              />

              <Button variant="contained" onClick={() => uploadFaq('ja')} sx={{ borderRadius: '4px' }}>
                {t('system_settings.save')}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* 이메일 템플릿 */}
        <Box
          padding={2}
          paddingBottom={2}
          borderRadius={1}
          bgcolor={theme.palette.contentBlock.background}
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ width: '100%' }}
        >
          <Box display="flex" flexDirection="row" gap={1} flexWrap="wrap">
            <Typography variant="h5" fontWeight="bold">
              {t('system_settings.email_template')}
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                // width: '100vw',
                fontSize: '5px',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              {/* 언어 선택 버튼 */}
              <ButtonGroup
                size="small"
                color="inherit"
              >
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',

                    bgcolor: isTemplateLanguageSelected('ko') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateLanguageSelected('ko') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText,
                    whiteSpace: 'normal', // 글자 줄바꿈 허용
                    wordBreak: 'break-word', // 긴 단어도 줄바꿈
                    maxWidth: '120px' // 최대 너비 설정
                  }}
                  onClick={() => handleTemplateLanguageChange('ko')}
                >
                  {t('system_settings.native')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateLanguageSelected('en') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateLanguageSelected('en') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText,
                    whiteSpace: 'normal', // 글자 줄바꿈 허용
                    wordBreak: 'break-word', // 긴 단어도 줄바꿈
                    maxWidth: '120px' // 최대 너비 설정
                  }}
                  onClick={() => handleTemplateLanguageChange('en')}
                >
                  {t('system_settings.foreigner')}
                </Button>
              </ButtonGroup>
              {/* 템플릿 타입 버튼 */}
              <ButtonGroup
                size="small"
                color="inherit"
              >
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('VERIFY_CODE') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('VERIFY_CODE') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('VERIFY_CODE')}
                >
                  {t('system_settings.email_template_btn.verify_code')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('VISIT_REQUEST') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('VISIT_REQUEST') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('VISIT_REQUEST')}
                >
                  {t('system_settings.email_template_btn.visit_request')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('VISIT_REQUEST_APPROVE') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('VISIT_REQUEST_APPROVE') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('VISIT_REQUEST_APPROVE')}
                >
                  {t('system_settings.email_template_btn.visit_request_approve')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('VISIT_REQUEST_REJECT') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('VISIT_REQUEST_REJECT') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('VISIT_REQUEST_REJECT')}
                >
                  {t('system_settings.email_template_btn.visit_request_reject')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('VISIT_REQUEST_CANCEL') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('VISIT_REQUEST_CANCEL') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('VISIT_REQUEST_CANCEL')}
                >
                  {t('system_settings.email_template_btn.visit_request_cancel')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('SECURITY_CARD_ISSUE') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('SECURITY_CARD_ISSUE') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('SECURITY_CARD_ISSUE')}
                >
                  {t('system_settings.email_template_btn.card_issue')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('PASSWORD_INITIALIZE') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('PASSWORD_INITIALIZE') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('PASSWORD_INITIALIZE')}
                >
                  {t('system_settings.email_template_btn.password_reset')}
                </Button>
                <Button
                  sx={{
                    borderRadius: '4px',
                    borderColor: 'black',
                    bgcolor: isTemplateTypeSelected('REMIND') ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
                    color: isTemplateTypeSelected('REMIND') ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
                  }}
                  onClick={() => handleTemplateTypeChange('REMIND')}
                >
                  {t('system_settings.email_template_btn.remind')}
                </Button>
              </ButtonGroup>
            </Box>
          </Box>

          {templateDescription(templateSelectedType, defaultRemindTime) || null}

          <Box display="flex" flexDirection="column" gap={1} bgcolor={theme.palette.contentBlock.main} padding={2} borderRadius={1} boxShadow={1}>
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('system_settings.mail_title')}
              </Typography>
              <TextField
                value={templateTitle}
                onChange={(e) => setTemplateTitle(e.target.value)}
                sx={{
                  flexGrow: 1
                }}
              />
            </Box>
          </Box>
          <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main}>
            <Tiptap ref={templateEditorRef} content={templateContent} maxHeight="600px" />
          </Box>

          {templateMsg && <Alert severity="success">{templateMsg}</Alert>}

          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={1} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveTemplate}
              sx={{ borderRadius: '4px', fontWeight: 'bold', width: { xs: '100%', sm: '100px', md: '100px', lg: '100px', xl: '100px' } }}
            >
              {t('system_settings.save')}
            </Button>

            <Button
              variant="contained"
              color="success"
              disabled={disableTemplateTest}
              onClick={handleTestTemplate}
              sx={{ borderRadius: '4px', fontWeight: 'bold', width: { xs: '100%', sm: '200px' } }}
            >
              {t('system_settings.send_test_mail_to_me')}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
